import React from 'react';
import { Link } from 'react-router-dom';
import { Coins, ShieldCheck, Cookie } from 'lucide-react';
import { openCookieSettings } from '../common/CookieConsent';
import { usePublicSettings } from '../../hooks/useApi';

const footCols = {
  Marketplace: [['Browse all', '/marketplace'], ['Sell a product', '/marketplace/new'], ['My orders', '/marketplace/orders'], ['Cart', '/cart']],
  Resources: [['Browse resources', '/resources'], ['Upload resource', '/resources/new'], ['My resources', '/resources/mine'], ['Blog', '/blog']],
  Community: [['Forums', '/forums'], ['Feed', '/feed'], ['Rankings', '/rankings'], ['Streaming', '/streaming']],
  'NUY & Account': [['NUY Wallet', '/wallet'], ['NUY Coin', '/nuy-coin'], ['Affiliates', '/affiliate'], ['NUYED Plus', '/plus']],
};

export default function Footer() {
  const { data: site } = usePublicSettings();
  const cleanUrl = (u) => (u && /^(https?:\/\/|\/)[^\s,]+$/.test(String(u).trim()) ? String(u).trim() : null);
  const logoUrl = cleanUrl(site?.logo_url);
  return (
    <footer className="relative mt-8 border-t border-white/[0.06] overflow-hidden" data-testid="site-footer">
      <div className="gx-grid-overlay opacity-60" />
      <div className="absolute -bottom-24 left-1/4 w-[420px] h-[260px] blur-[130px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(0,229,255,0.12),transparent 70%)' }} />
      <div className="relative px-4 md:px-8 max-w-[1600px] mx-auto py-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2 md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2 mb-4" data-testid="footer-logo">
              {logoUrl ? (
                <img src={logoUrl} alt={site?.site_name || 'NUYED'} className="h-9 max-w-[180px] object-contain" />
              ) : (
                <>
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-gx-cyan to-gx-purple flex items-center justify-center font-display font-black text-black text-lg">N</span>
                  <span className="font-display font-extrabold text-xl tracking-tight">{site?.site_name || 'Nuyed'}</span>
                </>
              )}
            </Link>
            <p className="text-sm text-gx-muted leading-relaxed max-w-xs mb-5">
              The modern nulled community. Buy &amp; sell accounts, digital goods and premium resources — every order protected by <span className="text-gx-cyan">escrow</span>.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.06] mb-5">
              <Coins className="w-3.5 h-3.5 text-gx-cyan" />
              <span className="text-xs text-gx-muted">Powered by the <span className="text-gx-text font-semibold">NUY</span> economy</span>
            </div>
            <div className="flex items-center gap-2.5">
              {[['x', 'X', 'https://x.com'], ['discord', 'Discord', 'https://discord.com'], ['telegram', 'Telegram', 'https://telegram.org']].map(([s, label, href]) => (
                <a key={s} href={href} target="_blank" rel="noopener noreferrer" title={label} className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/[0.03] border border-white/[0.06] text-gx-muted hover:text-gx-cyan hover:border-gx-cyan/40 transition">
                  <img src={`https://cdn.simpleicons.org/${s}/8B95A7`} className="w-4 h-4 gx-footer-social-ic" loading="lazy" alt={label} />
                </a>
              ))}
            </div>
          </div>
          {Object.entries(footCols).map(([heading, links]) => (
            <div key={heading}>
              <h4 className="font-display font-bold text-xs uppercase tracking-widest text-gx-text mb-4">{heading}</h4>
              <ul className="space-y-2.5">
                {links.map(([lbl, href]) => (
                  <li key={href}><Link to={href} className="text-sm text-gx-muted hover:text-gx-cyan transition">{lbl}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gx-muted">© {new Date().getFullYear()} NUYED. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gx-muted">
            <Link to="/terms" className="hover:text-gx-cyan transition">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-gx-cyan transition">Privacy Policy</Link>
            <Link to="/dmca" className="hover:text-gx-cyan transition">DMCA</Link>
            <Link to="/cookies" className="hover:text-gx-cyan transition">Cookie Policy</Link>
            <Link to="/refunds" className="hover:text-gx-cyan transition">Refunds &amp; Escrow</Link>
            <button type="button" onClick={openCookieSettings} className="inline-flex items-center gap-1 hover:text-gx-cyan transition" data-testid="footer-cookie-settings"><Cookie className="w-3 h-3" /> Cookie settings</button>
            <a href="mailto:support@nuyed.com" className="hover:text-gx-cyan transition">Contact</a>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gx-muted">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            Escrow protected
          </div>
        </div>
      </div>
    </footer>
  );
}
